import { useCallback, useEffect, useRef, useState } from "react";
import { isCurrentBuild } from "../release/staleBuild";
import { useMutation } from "@apollo/client/react";
import {
  CREATE_BOOKING,
  bookingFailureFrom,
  isAuthorizedPilotOutcome,
  type BookingFailure,
  type BookingOutcome,
  type CreateBookingData,
  type CreateBookingVariables,
} from "./operations";
import {
  beginBookingAttempt,
  isSameAttemptInput,
  isSameIdentity,
  withTermsAcceptance,
  type BookingAttempt,
} from "./attempt";
import type { TermsDecision } from "../terms/terms";
import { isValidRevision } from "./revision";
import { ensureGuestIdentity } from "../../lib/firebase";
import { UNKNOWN_OUTCOME_CLAIM_MS, useGuestIdentityClaim } from "../../lib/guestSession";
import { holdSessionEnd } from "../../auth/sessionEndGuard";
import { pastEvery, refusalCanBeDefinite, type SendFacts } from "./replayRule";

/**
 * What the client actually got, and it is not always an appointment.
 *
 * `createBooking` fans out by the professional's payment mode and confirmation
 * setting:
 *
 *   IN_PERSON_ONLY x auto    -> CONFIRMED + NONE                        booked
 *   IN_PERSON_ONLY x manual  -> REQUESTED + AWAIT_PROFESSIONAL_ACCEPTANCE
 *   DEPOSIT/FULL   x manual  -> REQUESTED + AWAIT_PROFESSIONAL_ACCEPTANCE
 *   DEPOSIT/FULL   x auto    -> COMPLETE_PAYMENT                money required
 *
 * The first two are September. Rendering the second as "You're booked" would be
 * a false statement to a client about whether they have an appointment, so the
 * two are separate outcomes here and get separate copy on screen.
 *
 * The fourth is not built — Product has not ruled on whether the pilot includes
 * deposits (Q34) — and it is rejected rather than approximated. `unauthorized`
 * is that rejection: the server answered, the answer is not one this cohort can
 * describe, and saying nothing true is better than saying something false.
 */
/**
 * HOW LONG AN ATTEMPT A CHECK COULD NOT RESOLVE KEEPS THE IDENTITY CLAIMED
 * (security audit F1): one slot-hold lifetime.
 *
 * The attempt is kept so the next Confirm asks under its key first (see
 * `unresolvedRef`), because a first send may still have been inside its
 * transaction when the Check was answered. That transaction runs on a hold
 * that lasts at most ten minutes, so by then it has certainly finished one way
 * or the other. After that the attempt is still asked about on the next
 * Confirm if the identity is still here, but it no longer holds the identity
 * against rotation: a tab left on this screen would otherwise keep a UID that
 * may own a booking for as long as it stays open.
 */
export const UNRESOLVED_CLAIM_MS = 10 * 60 * 1000;

export type BookingResult =
  | { kind: "confirmed"; outcome: BookingOutcome }
  | { kind: "requested"; outcome: BookingOutcome };

export type BookingState =
  | { status: "idle" }
  | { status: "sending" }
  | {
      status: "done";
      result: BookingResult;
      /**
       * The booking came back from re-asking an EARLIER attempt (see
       * `unresolvedRef`), not from the hold this Confirm was pressed on. That
       * newer hold was never consumed, so Book releases it.
       */
      resolvedPrior?: true;
      /**
       * The Firebase UID the booking was made under. Book retires that
       * identity once this outcome is on screen, if it is anonymous
       * (auth/guestRotation.ts). Never rendered, logged or reported.
       */
      uid?: string;
    }
  /** The server answered, and the answer contradicted itself or the selection. */
  | { status: "unauthorized" }
  /**
   * The commit did not produce a bookable outcome. `failure` says WHICH of the
   * documented outcomes it was — including whether we know anything at all.
   * A single flat `failed` cannot carry that, and flattening it is how a lost
   * response came to be reported as "nothing was booked and nothing was
   * charged" on a request that may well have committed.
   */
  | {
      status: "failed";
      failure: BookingFailure;
      /**
       * The refusal answered a CHECK after a lost response, not a first send.
       * The first send may have committed and consumed the hold, so a refusal
       * here is not proof that nothing was booked, and nothing may treat it as
       * a clean slate to book again from (Book's slot-refusal recovery).
       */
      afterCheck?: true;
    }
  /**
   * THE REQUEST MAY HAVE COMMITTED AND WE DID NOT HEAR THE ANSWER.
   *
   * Distinct from `failed` with `kind: "lost"`, which this replaces for
   * `createBooking`. That state could only advise; this one can ACT, because
   * `booking-v1@1.4.0` makes the mutation replayable under the attempt's
   * idempotency key. The frozen attempt is held here so the check resends the
   * identical request — never a new key, which would mint a second booking.
   *
   * `checking` drives the busy control. `checkAlsoLost` is true once an
   * explicit check ALSO lost its response, which changes the body copy and
   * nothing else: same state, same request, same key.
   */
  /**
   * IDENTITY COULD NOT BE ESTABLISHED, so NOTHING WAS SENT.
   *
   * Kept separate from `uncertain` deliberately, and this is the distinction
   * the Director's correction is about: `uncertain` means the request may have
   * committed, and its copy says "may have gone through". If a failure to sign
   * in anonymously produced that screen, we would be telling someone their
   * booking might exist when no mutation was ever dispatched — a false claim
   * on the most consequential screen in the flow, and one that invites them to
   * wait for an email that cannot arrive.
   *
   * No key is minted, no request leaves, and no booking claim is made.
   */
  | { status: "identity-unavailable" }
  /**
   * THE ATTEMPT COULD NOT BE CONSTRUCTED, so NOTHING WAS SENT.
   *
   * `beginBookingAttempt` refuses rather than improvises: no WebCrypto means no
   * idempotency key, and a key outside the contract's shape would be rejected
   * as `INVALID_INPUT` at exactly the moment recovery matters. Both are correct
   * refusals, and both happen BEFORE any mutation is dispatched.
   *
   * Kept separate from `identity-unavailable` because the cause is different —
   * identity was established here, the key was not — while the one fact that
   * reaches the screen is the same and is true of both: nothing was sent, so
   * nothing may have gone through, and "Try again" is safe because there is no
   * committed request to duplicate.
   */
  | { status: "cannot-start" }
  | {
      status: "uncertain";
      attempt: BookingAttempt;
      checking: boolean;
      checkAlsoLost: boolean;
    };

/** What the client believes they are booking, checked against what came back. */
export interface ExpectedBooking {
  professionalId: string;
  serviceId: string;
  startTime: string;
}

/**
 * ONE READING OF A `createBooking` RESPONSE, shared by the first send and the
 * recovery check.
 *
 * Written as a free function rather than inlined twice on purpose. The two
 * paths must agree exactly — the whole promise of the idempotency key is that
 * a replay is answered with the ORIGINAL result, so if the check interpreted a
 * response more loosely than the first send did, recovery would be the way to
 * get a claim the first send would have refused to make.
 */
export function interpret(
  outcome: BookingOutcome,
  expected: ExpectedBooking,
  /**
   * The appointment this attempt has already been told about, if any.
   *
   * Null on a first send — there is nothing to contradict yet. Non-null once a
   * response has named an appointment, after which a DIFFERENT id under the
   * same idempotency key is not a recovery but a response we cannot account
   * for.
   */
  knownAppointmentId: string | null
): BookingState {
  const appt = outcome.appointment;

  /*
   * REVISION FIRST, AND FAIL CLOSED.
   *
   * `Int!` in 1.4.0, so a missing, zero, negative or non-integer value is a
   * broken response rather than a state to absorb. Checked BEFORE the selection
   * match so a malformed projection can never be adopted as truth, and checked
   * here rather than at the ordering site so nothing downstream has to
   * re-establish it.
   */
  if (!isValidRevision(appt.revision)) return { status: "unauthorized" };

  // IS THIS THE BOOKING THEY ASKED FOR? Checked before anything is shown,
  // because a confirmation screen describing a different appointment than the
  // one the client chose is worse than an error.
  const matchesSelection =
    appt.professionalId === expected.professionalId &&
    appt.serviceId === expected.serviceId &&
    appt.startTime === expected.startTime;

  if (!matchesSelection || !isAuthorizedPilotOutcome(outcome)) {
    return { status: "unauthorized" };
  }

  // A replay must describe the SAME appointment the attempt already named. A
  // different id under our own key is not a recovery — it is a response we
  // cannot account for, and it makes no claim.
  if (knownAppointmentId !== null && knownAppointmentId !== appt.id) {
    return { status: "unauthorized" };
  }

  // The guard has already proved this is one of exactly two pairings, so the
  // status alone now determines which. Compared against CONFIRMED rather than
  // switched, so nothing new can fall through to success.
  return {
    status: "done",
    result: { kind: appt.status === "CONFIRMED" ? "confirmed" : "requested", outcome },
  };
}

/**
 * A refusal of the INPUT itself (the contact, or any argument). Validation
 * that deterministic means nothing could have committed under that input, so
 * the frozen attempt is not a booking that may exist; it is just wrong, and
 * the client has to be able to send a corrected one (QE P2 on b22e0d4: a
 * contact refusal answering a Check kept re-sending the old email forever).
 *
 * A Terms refusal (`kind: "terms"`) is deliberately not one of these. The
 * Terms are outside the fingerprint and the next press replaces them, so the
 * attempt's own request is not "wrong" and is re-sent under its own key.
 */
const monoNow = () => performance.now();

/** The lost sends under one key, and the expiry of the hold that key was made on (rc.11 1c). */
interface LostSends {
  readonly key: string;
  readonly holdExpiresAt: number;
  readonly lost: readonly SendFacts[];
}

/** The facts of a send under `key`, stamped now. An unknown hold: NaN, never definite. */
function sendFacts(record: LostSends | null, key: string): SendFacts {
  return { mono: monoNow(), at: Date.now(), holdExpiresAt: record?.key === key ? record.holdExpiresAt : Number.NaN };
}

/** The record with one more lost send under its own key; any other key changes nothing. */
function withLost(record: LostSends | null, key: string, sent: SendFacts): LostSends | null {
  return record?.key === key ? { ...record, lost: [...record.lost, sent] } : record;
}

/** Backend's rule: allow-listed, past the threshold of EVERY lost send under the key. */
function isDefiniteNone(record: LostSends | null, key: string, sent: SendFacts, failure: BookingFailure): boolean {
  return record?.key === key && record.lost.length > 0 && pastEvery(sent.mono, record.lost) && refusalCanBeDefinite(failure);
}

/** The session itself was refused: the identity cannot ask, which says nothing about the booking. */
const sessionRefused = (failure: BookingFailure) => failure.kind === "refused" && failure.code === "UNAUTHENTICATED";


export function useBooking() {
  const [state, setState] = useState<BookingState>({ status: "idle" });
  /**
   * The attempt currently in flight or awaiting resolution.
   *
   * A ref rather than state because `commit` reads it synchronously to decide
   * whether this is the same logical attempt, and a state read there would see
   * the value from before the last render — the same class of bug the `sending`
   * latch below exists to prevent, one level up.
   */
  const attemptRef = useRef<BookingAttempt | null>(null);
  /**
   * The current state, readable synchronously.
   *
   * `checkResult` must refuse to run unless the screen is genuinely in
   * `uncertain`, and it must read that at DISPATCH time. Closing over `state`
   * would capture the value from the render that created the callback, which is
   * how a stale closure lets a check fire against a state that has already
   * moved on.
   */
  const stateRef = useRef<BookingState>(state);
  /** Until when the unknown outcome is claimed; null once its UID is gone (N1, N2). */
  const [uncertainClaimUntil, setUncertainClaimUntil] = useState<number | null>(null);
  /**
   * Move to `next`, updating the synchronous mirror in the SAME statement.
   *
   * This replaces a bare `stateRef.current = state` in the render body. That
   * write was the defect class this batch's sibling hook had just been
   * corrected for: React may render a component without committing, and with
   * the React Compiler enabled it may skip a render's body entirely when it
   * memoizes — either way a ref written during render can hold a value that no
   * committed tree ever had, or miss one it did.
   *
   * Pairing the two here is stronger than moving the write into an effect. The
   * ref is updated at the exact moment the transition is requested, from an
   * event handler or an async continuation and never from render, so a
   * dispatch-time read cannot observe a value older than the last transition
   * this hook asked for — including two transitions in one tick, which an
   * effect would collapse.
   *
   * Every transition in this hook goes through here. A `setState` that skipped
   * it would leave the mirror behind, which is precisely the stale read
   * `checkResult` must never make.
   */
  /*
   * A SESSION END WAITS WHILE A BOOKING IS SENDING OR ITS OUTCOME IS UNKNOWN
   * (auth/sessionEndGuard.ts). Taken and released HERE, at the transition,
   * not in an effect: a revocation answered in the same tick as Confirm must
   * already find the hold. Released on unmount: leaving the screen lets the
   * deferred sign-out run, and the attempt is no longer on screen to resend.
   */
  const sessionEndHold = useRef<(() => void) | null>(null);
  useEffect(() => () => {
    sessionEndHold.current?.();
    sessionEndHold.current = null;
  }, []);
  const setBookingState = useCallback((next: BookingState) => {
    // ENTERING an unknown outcome starts its bounded claim (security audit N1).
    if (next.status === "uncertain" && stateRef.current.status !== "uncertain") {
      setUncertainClaimUntil(Date.now() + UNKNOWN_OUTCOME_CLAIM_MS);
    }
    stateRef.current = next;
    const mayExist = next.status === "sending" || next.status === "uncertain";
    // An OUTCOME keeps the hold until the screen showing it has taken its
    // own (Book's layout-effect hold on Review): released by the passive
    // effect below, which runs after that layout effect. Releasing here
    // would drain a queued sign-out before "You're booked" ever renders.
    const handOver = next.status === "done" || next.status === "unauthorized";
    if (mayExist && !sessionEndHold.current) sessionEndHold.current = holdSessionEnd();
    else if (!mayExist && !handOver && sessionEndHold.current) {
      const release = sessionEndHold.current;
      sessionEndHold.current = null;
      release();
    }
    setState(next);
  }, []);
  // The hand-over's release: passive, so after every layout effect of the
  // commit that rendered the outcome.
  useEffect(() => {
    if (state.status === "sending" || state.status === "uncertain") return;
    const release = sessionEndHold.current;
    sessionEndHold.current = null;
    release?.();
  }, [state.status]);
  /** The appointment id this attempt has been told about, once it has been. */
  const knownAppointmentRef = useRef<string | null>(null);
  /**
   * What the FIRST send was told to expect, held for the recovery check.
   *
   * The check must compare a replayed response against the same expectation the
   * original send used. Re-deriving it at check time reads live state — a hold
   * that has since expired, a selection the visitor may have edited — and a
   * drifted expectation makes the attempt's own booking look like a different
   * appointment, which fails closed and strands a booking that exists. Frozen
   * for the same reason the input is.
   */
  const expectedRef = useRef<ExpectedBooking | null>(null);
  /**
   * AN ATTEMPT A CHECK COULD NOT RESOLVE, carried until it is resolved.
   *
   * A Check that comes back hold-gone is very probably "nothing committed
   * under this key": Backend replays a committed key before it validates the
   * hold. The one case it cannot rule out is a Check that raced a first send
   * still inside its transaction (Web Director A2, 2026-09-26). So the next
   * Confirm — an explicit press, never an effect — asks under the OLD key
   * first. A booking comes back: that is the client's booking, shown, and no
   * new attempt is made. A definite refusal: nothing is there, and the new
   * attempt proceeds under a new key. Lost: back to `uncertain` on the old
   * attempt. Survives `reset()` on purpose; only an answer clears it.
   */
  const unresolvedRef = useRef<BookingAttempt | null>(null);
  /**
   * Until when the identity claim below covers `unresolvedRef`; written only
   * beside it. Bounded (security audit F1): see `UNRESOLVED_CLAIM_MS`.
   */
  const [unresolvedUntil, setUnresolvedUntil] = useState<number | null>(null);
  const holdUnresolved = useCallback((attempt: BookingAttempt | null) => {
    unresolvedRef.current = attempt;
    setUnresolvedUntil(attempt === null ? null : Date.now() + UNRESOLVED_CLAIM_MS);
  }, []);
  /** Synchronous latch. A `setState` updater does not run at dispatch time, so
   *  reading the previous status there lets two clicks in one tick both pass —
   *  which produced two cancellations on the management screen before it was
   *  found by a real double tap, and two `cancelAppointmentByAccessToken` calls
   *  in the September pilot branch when the same guard was written as state. */
  const sending = useRef(false);
  const [createBooking] = useMutation<CreateBookingData, CreateBookingVariables>(CREATE_BOOKING);
  /*
   * rc.11 1c: THE SENDS UNDER A KEY WHOSE ANSWER WAS LOST, and the expiry of
   * the hold that key was made on (features/booking/replayRule.ts). A replay's
   * refusal means "no booking under this key" only on Backend's allow-list,
   * under the same uid, and past the threshold of EVERY lost send under the
   * key, the latest included (a Check or a re-ask is itself a send that can
   * commit). Until then the uncertainty stays and NO NEW KEY is minted. Never
   * sent, never logged.
   */
  const lostSends = useRef<LostSends | null>(null);
  // Read and written only inside the callbacks below, through module
  // functions (reviewer P3 on 24595392: no helper closures in the hook body).
  /*
   * THE UID IS NOT OURS TO LOSE WHILE THE ANSWER IS OPEN (security audit M1).
   * A commit in flight, an unknown outcome, and an attempt a Check could not
   * resolve are all reachable only under the UID that made them: keys are
   * account-scoped. While any of them exists the guest identity is claimed and
   * no rotation, after an outcome or for idleness, may retire it.
   *
   * A commit in flight for as long as it lasts. An unknown outcome for
   * `UNKNOWN_OUTCOME_CLAIM_MS` from entering it, renewed by each Check press,
   * and only while the current UID is still the attempt's (N1, N2): a Check
   * that finds a different identity releases it, so the claim never holds an
   * identity the attempt cannot use. The unresolved attempt for a bounded window.
   */
  useGuestIdentityClaim(state.status === "sending");
  useGuestIdentityClaim(
    state.status === "uncertain" && uncertainClaimUntil !== null,
    uncertainClaimUntil ?? undefined,
    UNKNOWN_OUTCOME_CLAIM_MS
  );
  useGuestIdentityClaim(unresolvedUntil !== null, unresolvedUntil ?? undefined);

  /** Interpret, and remember the appointment id the first answer named. */
  const applyOutcome = useCallback((outcome: BookingOutcome, expected: ExpectedBooking, uid: string) => {
    const next = interpret(outcome, expected, knownAppointmentRef.current);
    if (next.status !== "done") return next;
    knownAppointmentRef.current = outcome.appointment.id;
    return { ...next, uid };
  }, []);

  const commit = useCallback(
    async (
      holdId: string,
      expected: ExpectedBooking,
      /**
       * What the person decided about the Terms shown on Review. REQUIRED, so a
       * caller cannot send without having asked: a caller holding no decision
       * (terms loading, unreadable, or published and unticked) has nothing to
       * pass and must not call this.
       */
      terms: TermsDecision,
      /**
       * rc.10: the policy re-read, asked once identity is known and BEFORE
       * anything is sent (the old key's re-ask included). False stops the
       * press: nothing is sent and the screen goes back to what it showed (the
       * caller says why).
       */
      beforeSend?: () => Promise<boolean>,
      /** The hold's expiry as the server sent it (rc.11 1c): what a later replay is judged against. */
      holdExpiresAt?: string | null
    ) => {
      if (sending.current) return;
      sending.current = true;
      // What the screen showed before this press, restored if a newer
      // release stops it (review of d8f834c, P2): a Check's uncertain
      // Terms refusal must not turn into a clean "idle" Review.
      const before = stateRef.current;

      /*
       * SNAPSHOT THE EXPECTATION SYNCHRONOUSLY, BEFORE ANY AWAIT.
       *
       * `beginBookingAttempt` copies it, but it runs after `ensureGuestIdentity`
       * resolves — so a caller mutating its own object during that window would
       * have the mutation frozen INTO the attempt, and the attempt's own booking
       * would then be refused as a different appointment. Measured: the
       * in-flight mutation test failed in exactly that way.
       *
       * Copying on entry closes the window. The caller's object is never
       * retained and never read again after this line.
       */
      const snapshot = {
        professionalId: expected.professionalId,
        serviceId: expected.serviceId,
        startTime: expected.startTime,
      };

      /*
       * ONE KEY PER LOGICAL ATTEMPT, MINTED BEFORE THE FIRST SEND.
       *
       * Reused when this is the same hold, so a caller that re-enters `commit`
       * for the same intent replays rather than creating a second booking. A
       * NEW hold is a genuinely different attempt and gets a new key — the
       * contract refuses one key over different arguments, and it is right to.
       *
       * Minted here rather than in the component: a key created during render
       * is created again on every render, and a key created in an effect exists
       * only after one has run. Both produce exactly the duplicate this closes.
       */
      setBookingState({ status: "sending" });


      /*
       * IDENTITY FIRST, AND BEFORE THE ATTEMPT EXISTS AT ALL.
       *
       * The hold is owned by the verified uid on the token, and — the reason
       * this moved ahead of the key — booking receipts are scoped
       * to the account. An attempt minted before we know who we are is an
       * attempt nobody can look up.
       *
       * Re-established here rather than relied on from the hold step: a session
       * can be lost in between (a private-mode eviction, a refresh failure),
       * and re-establishing costs nothing when one already exists.
       *
       * A FAILURE HERE IS PRE-DISPATCH. Nothing was sent, so the screen must
       * not say anything may have gone through.
       */
      let uid: string;
      try {
        const user = await ensureGuestIdentity();
        uid = user.uid;
        if (!uid) throw new Error("identity resolved without a uid");
      } catch {
        sending.current = false;
        setBookingState({ status: "identity-unavailable" });
        return;
      }

      /*
       * THE POLICY IS READ AGAIN BEFORE ANYTHING IS SENT (rc.10), the old
       * key's re-ask below included: after a Check's Terms refusal that
       * re-ask can make the booking (review of 1d29dc8b, P1). Unlike the build
       * check, a policy stop strands nothing: the next press is the retry. So
       * it comes first, and a stop puts the screen back exactly as it was (a
       * Check's uncertainty and its Terms refusal stay; nothing was asked).
       */
      if (beforeSend && !(await beforeSend())) {
        sending.current = false;
        setBookingState(before);
        return;
      }

      /*
       * RESOLVE AN UNRESOLVED EARLIER ATTEMPT FIRST (see `unresolvedRef`).
       *
       * Same identity only: keys are account-scoped, so under a different uid
       * the old key names nothing this caller can ask about, and holding the
       * client on it would be a trap with no way out.
       */
      const prior = unresolvedRef.current;
      // What the old attempt's question settled, for the newer-release stop
      // below: Backend's hold-level answer, or the attempt dropped as not
      // this caller's or this page's. Either way the screen it was made on
      // (a Check's uncertainty, a Terms refusal) no longer says what is true.
      let settled: { failure: BookingFailure } | "dropped" | null = null;
      if (prior) {
        // A different identity: keys are account-scoped, so the old key names
        // nothing this caller can ask. A different professional (Book is not
        // keyed by slug, so in-app history can carry this hook to another
        // page): the old attempt is not this page's to replay (review A4b).
        if (!isSameIdentity(prior, uid) || prior.expected.professionalId !== snapshot.professionalId) {
          holdUnresolved(null);
          settled = "dropped";
        } else {
          /*
           * THE OLD KEY, CARRYING THE TERMS THE PERSON JUST ACCEPTED.
           *
           * The Terms are outside the fingerprint, so this is still the old
           * attempt's request. It matters when the Check was refused as
           * TERMS_STALE: the re-ask on the SAME hold is this very replay, under
           * the same key. Re-sending the old Terms would be refused again, and
           * continuing as new would mint a second key for the same hold.
           */
          const asked = withTermsAcceptance(prior, terms.acceptance);
          const key = asked.input.idempotencyKey;
          const sent = sendFacts(lostSends.current, key);
          try {
            const { data } = await createBooking({ variables: { input: asked.input } });
            const outcome = data?.createBooking;
            attemptRef.current = asked;
            expectedRef.current = asked.expected;
            if (!outcome) {
              lostSends.current = withLost(lostSends.current, key, sent);
              holdUnresolved(asked);
              sending.current = false;
              setBookingState({ status: "uncertain", attempt: asked, checking: false, checkAlsoLost: true });
              return;
            }
            // It exists. Show it; never make a second one.
            holdUnresolved(null);
            sending.current = false;
            const next = applyOutcome(outcome, asked.expected, asked.uid);
            // `resolvedPrior`: Book releases the hold this press was made on.
            // Not keyed on the hold id: Backend may answer the same owner and
            // window with the same id (Book.tsx). When the old attempt was on
            // this very hold (the Terms re-ask), the release frees that hold's
            // consumed document, which the app-paid auto-confirm strategies
            // leave behind; the appointment itself keeps the slot (reviewer P3).
            setBookingState(next.status === "done" ? { ...next, resolvedPrior: true } : next);
            return;
          } catch (error) {
            const failure = bookingFailureFrom(error);
            if (failure.kind === "lost") {
              lostSends.current = withLost(lostSends.current, key, sent);
              holdUnresolved(asked);
              attemptRef.current = asked;
              expectedRef.current = asked.expected;
              sending.current = false;
              setBookingState({ status: "uncertain", attempt: asked, checking: false, checkAlsoLost: true });
              return;
            }
            /*
             * ONLY A DEFINITE ANSWER SAYS NOTHING IS THERE (rc.11 1c, Backend's
             * rule; it replaces review A1's hold-level list). Then, and only
             * then, the new attempt proceeds under a new key. Otherwise:
             * - a Terms refusal keeps rc.8's re-ask under this SAME key (the
             *   booking may still exist, so Review keeps the uncertainty
             *   notice, WEB-TOS-03);
             * - an existing booking is shown, never retried;
             * - anything else (too early, another identity, a refusal from
             *   before the receipt read, an unknown code) proves nothing: back
             *   to the uncertainty on THIS attempt. No new key.
             */
            if (!isDefiniteNone(lostSends.current, key, sent, failure)) {
              attemptRef.current = asked;
              expectedRef.current = asked.expected;
              sending.current = false;
              if (failure.kind === "terms" || failure.kind === "already-booked" || sessionRefused(failure)) {
                if (failure.kind === "terms") holdUnresolved(asked);
                setBookingState(failure.kind === "terms" ? { status: "failed", failure, afterCheck: true } : { status: "failed", failure });
                return;
              }
              holdUnresolved(asked);
              setBookingState({ status: "uncertain", attempt: asked, checking: false, checkAlsoLost: false });
              return;
            }
            holdUnresolved(null);
            if (attemptRef.current === prior) attemptRef.current = null;
            settled = { failure };
          }
        }
      }

      /*
       * NOT A NEW BOOKING FROM AN OLD BUILD (rc.9 item 2,
       * features/release/staleBuild.ts). Asked here, AFTER the old key's
       * replay above and BEFORE a new attempt is minted. The replay resolves
       * a request only this page holds, the same class as the Check, which a
       * reload would strand (review of 14a272f, follow-up 1); a NEW booking
       * from an old build is what Backend's R-4 refuses. The state says
       * "sending" throughout, so a session end raised meanwhile waits for it.
       * When a newer release is live, nothing is sent and the screen goes
       * back to what it showed, where the notice offers a reload. Unless the
       * replay just settled the old attempt (review of ea16b8fc, P2-2):
       * restoring a Check's uncertainty or its Terms refusal would claim a
       * booking may exist after Backend said none does, and keep the page
       * busy so the notice never shows. Then the screen shows Backend's
       * answer, or a plain Review when the old attempt was dropped.
       */
      if (!(await isCurrentBuild())) {
        sending.current = false;
        setBookingState(settled === null ? before : settled === "dropped" ? { status: "idle" } : { status: "failed", failure: settled.failure });
        return;
      }

      /*
       * CONSTRUCTING THE ATTEMPT IS ITSELF PRE-DISPATCH, AND IT CAN FAIL.
       *
       * `beginBookingAttempt` throws on a browser with no WebCrypto and on a
       * generated key outside the contract's shape. Both are correct refusals.
       * Neither used to be caught: this ran OUTSIDE the try below, so a throw
       * escaped the `finally` that clears the latch. `sending.current` stayed
       * true for the life of the page, every later activation returned at the
       * first line of `commit`, the screen never left `sending`, and the throw
       * surfaced as an unhandled rejection.
       *
       * The unwind has to be complete: clear the latch, leave the screen on a
       * state that is TRUE (nothing was sent), and mint nothing. It must not
       * reach `uncertain` — no request was dispatched, so "may have gone
       * through" would be false — and it must not leave a half-built attempt
       * behind, so the refs are only written once construction has succeeded.
       */
      const existing = attemptRef.current;
      let attempt: BookingAttempt;
      try {
        /*
         * THE TERMS DO NOT DECIDE WHETHER THIS IS THE SAME ATTEMPT.
         *
         * `isSameAttemptInput` compares the hold, the field Backend
         * fingerprints for a signed-in client. The Terms ride on the attempt
         * the comparison chose, so a re-ask after TERMS_STALE (same hold, new
         * Terms) re-sends under the SAME key. A booking that key committed
         * then replays; nothing can mint a second one (Web Director, rc.8).
         */
        attempt = withTermsAcceptance(
          existing &&
            isSameAttemptInput(existing, holdId) &&
            isSameIdentity(existing, uid)
            ? existing
            : beginBookingAttempt(holdId, uid, snapshot),
          terms.acceptance
        );
      } catch {
        sending.current = false;
        setBookingState({ status: "cannot-start" });
        return;
      }
      attemptRef.current = attempt;
      if (attempt !== existing) expectedRef.current = attempt.expected;
      // The hold this key is made on (rc.11 1c), recorded once per key.
      if (lostSends.current?.key !== attempt.input.idempotencyKey) {
        lostSends.current = { key: attempt.input.idempotencyKey, holdExpiresAt: holdExpiresAt ? Date.parse(holdExpiresAt) : Number.NaN, lost: [] };
      }
      const sent = sendFacts(lostSends.current, attempt.input.idempotencyKey);

      try {

        const { data } = await createBooking({ variables: { input: attempt.input } });

        const outcome = data?.createBooking;
        if (!outcome) {
          lostSends.current = withLost(lostSends.current, attempt.input.idempotencyKey, sent);
          // A 200 carrying neither `createBooking` nor a thrown error.
          //
          // This used to synthesise a `refused` failure, on the reasoning that
          // "the server answered, so this is definite". That is the exact
          // heuristic the allowlist in `operations.ts` exists to replace, and
          // it was reintroduced here, bypassing the allowlist entirely — so the
          // 19 tests pinning ambiguity never covered the runtime path. Caught in
          // review, not by the suite.
          //
          // An empty or truncated 200 — a proxy trimming a body, a serialiser
          // failing after the write — tells us nothing about whether the
          // appointment exists. It is the ambiguous case, and the screen must
          // not offer a retry or claim nothing happened.
          setBookingState({ status: "uncertain", attempt, checking: false, checkAlsoLost: false });
          return;
        }

        setBookingState(applyOutcome(outcome, attempt.expected, attempt.uid));
      } catch (error) {
        // WHICH failure this was decides what the screen may claim. See
        // `bookingFailureFrom` — the default is "we do not know", and only
        // evidence of a server answer moves a failure out of it.
        //
        // A LOST ANSWER IS NO LONGER TERMINAL. Under 1.4 the attempt has a
        // name, so "we do not know" is a question the client can now ASK. It
        // becomes `uncertain`, holding the frozen request; every other failure
        // is still a failure and still says only what it can prove.
        const failure = bookingFailureFrom(error);
        if (failure.kind === "lost") {
          lostSends.current = withLost(lostSends.current, attempt.input.idempotencyKey, sent);
          setBookingState({ status: "uncertain", attempt, checking: false, checkAlsoLost: false });
        } else {
          setBookingState({ status: "failed", failure });
        }
      } finally {
        sending.current = false;
      }
    },
    [createBooking, applyOutcome, setBookingState, holdUnresolved]
  );

  /**
   * ASK AGAIN, UNDER THE SAME NAME. The only way out of `uncertain`.
   *
   * Explicit: nothing calls this but a person activating the control. No
   * effect, timer, reconnect, visibility change, mount or render may reach it —
   * an automatic replay is a request the client cannot account for, and the one
   * thing worse than not knowing whether a booking exists is silently making a
   * second one while finding out.
   *
   * It resends the FROZEN input, key included. Not a rebuilt one: rebuilding
   * from current state would send different arguments under the same key, which
   * the server refuses as `IDEMPOTENCY_CONFLICT` — and a conflict here is
   * indistinguishable, to the person waiting, from having double-booked.
   *
   * A SECOND LOST RESPONSE CHANGES THE COPY AND NOTHING ELSE. Same state, same
   * attempt, same key, control restored. Resetting to `idle` here would drop
   * the only handle on a booking that may exist.
   */
  const checkResult = useCallback(
    async () => {
      if (sending.current) return;
      const current = stateRef.current;
      if (current.status !== "uncertain") return;
      const { attempt } = current;
      // No frozen expectation means no attempt was ever sent from here, so
      // there is nothing to check and nothing that may be claimed. The
      // expectation itself now travels on the attempt; this is only the
      // "was anything dispatched" marker.
      if (!expectedRef.current) return;

      sending.current = true;
      // Each press renews the claim (N1): someone is here, asking.
      setUncertainClaimUntil(Date.now() + UNKNOWN_OUTCOME_CLAIM_MS);
      setBookingState({ status: "uncertain", attempt, checking: true, checkAlsoLost: current.checkAlsoLost });

      let sent: SendFacts | null = null;
      try {
        /*
         * THE IDENTITY MUST STILL BE THE ONE THAT MADE THE ATTEMPT.
         *
         * Keys are account-scoped, so this key under a different uid does not
         * name this attempt. Replaying it there would either create a second
         * booking under the new identity or be refused — and both are worse
         * than declining to ask.
         *
         * An anonymous session is exactly the kind that can be replaced without
         * anyone noticing: a private-mode eviction or a failed restore hands
         * back a NEW anonymous uid, precisely when someone is recovering.
         *
         * On a mismatch: no mutation, no new key, no claim. The attempt is
         * PRESERVED rather than reset, because the booking it names may still
         * exist and this is the only handle on it. The screen keeps the copy it
         * already has; nothing new is invented.
         */
        const identity = await ensureGuestIdentity().catch(() => null);
        if (!isSameIdentity(attempt, identity?.uid ?? null)) {
          /*
           * PRESERVED EXACTLY AS IT WAS, including `checkAlsoLost`.
           *
           * A uid mismatch is not a lost response, so flipping the copy to
           * "Lura still hasn't confirmed the result" would describe a check
           * that never happened. The screen keeps whichever body it already
           * had; Product has not written copy for "we can no longer identify
           * you", and inventing one here is not mine to do.
           *
           * The claim goes (N2): it would now hold the identity just read,
           * which the attempt cannot use, for as long as the page stays open.
           */
          setUncertainClaimUntil(null);
          // Nor the claim for a kept attempt (security re-audit L1): the
          // attempt is kept as the only handle on its booking, but its claim
          // would hold an identity it cannot use.
          setUnresolvedUntil(null);
          setBookingState({
            status: "uncertain",
            attempt,
            checking: false,
            checkAlsoLost: current.checkAlsoLost,
          });
          return;
        }
        // IDENTICAL VARIABLES. The same frozen object the first send used.
        sent = sendFacts(lostSends.current, attempt.input.idempotencyKey);
        const { data } = await createBooking({ variables: { input: attempt.input } });
        const outcome = data?.createBooking;
        if (!outcome) {
          lostSends.current = withLost(lostSends.current, attempt.input.idempotencyKey, sent);
          setBookingState({ status: "uncertain", attempt, checking: false, checkAlsoLost: true });
          return;
        }
        // The server answered for this key: an attempt kept under it (a Terms
        // re-ask's) is resolved, and its claim must not hold the identity a
        // definite outcome is about to retire (security re-audit L1).
        if (unresolvedRef.current?.input.idempotencyKey === attempt.input.idempotencyKey) holdUnresolved(null);
        setBookingState(applyOutcome(outcome, attempt.expected, attempt.uid));
      } catch (error) {
        const failure = bookingFailureFrom(error);
        if (failure.kind === "lost") {
          if (sent) lostSends.current = withLost(lostSends.current, attempt.input.idempotencyKey, sent);
          setBookingState({ status: "uncertain", attempt, checking: false, checkAlsoLost: true });
        } else if (sent && isDefiniteNone(lostSends.current, attempt.input.idempotencyKey, sent, failure)) {
          // rc.11 1c: definite, so nothing is under this key. The screen
          // follows the refusal's own kind, as for a first send's answer.
          holdUnresolved(null);
          setBookingState({ status: "failed", failure });
        } else if (failure.kind === "terms" || failure.kind === "already-booked" || sessionRefused(failure)) {
          // A Terms refusal: the first send may still be inside its
          // transaction, so the attempt stays unresolved and the next press
          // asks under the SAME key (the Terms then accepted) before any new
          // one could be minted. An existing booking: shown, never retried.
          // The session itself refused (UNAUTHENTICATED): this identity can
          // no longer ask. The attempt is kept for the next Confirm after
          // signing back in (the old key first), and the state resolves so
          // the deferred sign-out can run. None of these is "none".
          holdUnresolved(attempt);
          setBookingState({ status: "failed", failure, afterCheck: true });
        } else {
          // rc.11 1c: proves nothing (too early, before the receipt read, an
          // input refusal, an unknown code). The uncertainty stays, with its
          // Check, and the attempt is NOT dropped.
          setBookingState({ status: "uncertain", attempt, checking: false, checkAlsoLost: current.checkAlsoLost });
        }
      } finally {
        sending.current = false;
      }
    },
    [createBooking, applyOutcome, setBookingState, holdUnresolved]
  );

  const reset = useCallback(() => {
    attemptRef.current = null;
    knownAppointmentRef.current = null;
    expectedRef.current = null;
    setBookingState({ status: "idle" });
  }, [setBookingState]);

  /**
   * ANOTHER PERSON IS HERE NOW (rc.11 1c, the Web Director). Everything held
   * for a different uid leaves the page: the attempt, the old key's re-ask,
   * its lost sends and any outcome. A receipt is uid-scoped, so asking under
   * the new uid would only mislead, and the attempt carries the previous
   * person's booking facts. This is privacy, not a "no booking" claim: the
   * previous person's confirmation email is their ground truth. The same uid
   * keeps everything (the old key is asked first, no new key).
   */
  const forgetFor = useCallback((uid: string) => {
    const current = stateRef.current;
    const held = [attemptRef.current, unresolvedRef.current, current.status === "uncertain" ? current.attempt : null];
    const foreign = held.some((attempt) => attempt !== null && !isSameIdentity(attempt, uid)) ||
      (current.status === "done" && current.uid !== undefined && current.uid !== uid);
    if (!foreign) return;
    attemptRef.current = null;
    knownAppointmentRef.current = null;
    expectedRef.current = null;
    lostSends.current = null;
    holdUnresolved(null);
    setUncertainClaimUntil(null);
    setBookingState({ status: "idle" });
  }, [setBookingState, holdUnresolved]);

  return { state, commit, checkResult, reset, forgetFor };
}
